import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, determineURL } from '../services/api';

const AuthContext = createContext(null);

const ROLE_HIERARCHY = {
  ROLE_ADMIN: 5,
  ROLE_MANAGER: 4,
  ROLE_SUPERVISOR: 3,
  ROLE_STAFF: 2,
  ROLE_USER: 1,
};

/**
 * Determine the highest role from a list of basic roles.
 * Matches portalv2 DataService.loadUserPreferences() logic.
 */
function resolveHighestRole(basicRoles) {
  let highest = '';
  let highestLevel = 0;

  for (const role of basicRoles) {
    const level = ROLE_HIERARCHY[role] || 0;
    if (level > highestLevel) {
      highest = role;
      highestLevel = level;
    }
  }

  return highest;
}

const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const IONIC_DB_NAME = '1risk';
const IONIC_STORE_NAME = '_ionickv';

/**
 * Write key/value pairs to the Ionic Storage IndexedDB (_ionickv)
 * so that legacy Ionic apps launched from the portal can read session data.
 */
function openIonicDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IONIC_DB_NAME, 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(IONIC_STORE_NAME)) {
        db.createObjectStore(IONIC_STORE_NAME);
      }
    };
    request.onsuccess = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(IONIC_STORE_NAME)) {
        const version = db.version;
        db.close();
        const deleteReq = indexedDB.deleteDatabase(IONIC_DB_NAME);
        deleteReq.onsuccess = () => {
          const retry = indexedDB.open(IONIC_DB_NAME, version + 1);
          retry.onupgradeneeded = (ev) => {
            ev.target.result.createObjectStore(IONIC_STORE_NAME);
          };
          retry.onsuccess = (ev) => resolve(ev.target.result);
          retry.onerror = () => reject(retry.error);
        };
        deleteReq.onerror = () => reject(deleteReq.error);
      } else {
        resolve(db);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

function writeToIonicStorage(entries) {
  return openIonicDB().then((db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IONIC_STORE_NAME, 'readwrite');
      const store = tx.objectStore(IONIC_STORE_NAME);
      for (const [key, value] of entries) {
        store.put(value, key);
      }
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  });
}

/**
 * Returns true if the 1risk IndexedDB exists AND contains a 'user' key in _ionickv.
 * Falls back gracefully when indexedDB.databases() is unavailable.
 */
export function checkIonicSession() {
  const checkUserKey = () => {
    return new Promise((resolve) => {
      const request = indexedDB.open(IONIC_DB_NAME);
      request.onupgradeneeded = (e) => {
        // DB was just created — no valid session exists
        e.target.transaction.abort();
        resolve(false);
      };
      request.onsuccess = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(IONIC_STORE_NAME)) {
          db.close();
          resolve(false);
          return;
        }
        const tx = db.transaction(IONIC_STORE_NAME, 'readonly');
        const req = tx.objectStore(IONIC_STORE_NAME).get('user');
        req.onsuccess = () => {
          db.close();
          resolve(req.result !== undefined && req.result !== null);
        };
        req.onerror = () => { db.close(); resolve(false); };
      };
      request.onerror = () => resolve(false);
    });
  };

  if (typeof indexedDB.databases !== 'function') {
    return checkUserKey();
  }
  return indexedDB.databases().then((dbs) => {
    if (!dbs.some((db) => db.name === IONIC_DB_NAME)) return false;
    return checkUserKey();
  });
}

export function clearIonicStorage() {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(IONIC_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}

function clearSession() {
  localStorage.removeItem('portalUser');
  localStorage.removeItem('portalToken');
  localStorage.removeItem('portalActiveApi');
  localStorage.removeItem('portalOperation');
  localStorage.removeItem('portalTitlebarImage');
  localStorage.removeItem('portalLoginAt');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const storedUser = localStorage.getItem('portalUser');
        const storedToken = localStorage.getItem('portalToken');
        const loginAt = localStorage.getItem('portalLoginAt');

        if (storedUser && storedToken) {
          const expired = !loginAt || (Date.now() - Number(loginAt)) > SESSION_MAX_AGE_MS;
          if (expired) {
            clearSession();
          } else {
            const ionicDBExists = await Promise.race([
              checkIonicSession(),
              new Promise((resolve) => setTimeout(() => resolve(true), 3000)),
            ]);
            if (!ionicDBExists) {
              // Legacy app logged out (deleted the DB) — invalidate portal session too
              sessionStorage.setItem('portalSessionMessage', 'You have been signed out by another 1RISK application. Please log in again.');
              clearSession();
            } else {
              try {
                setUser(JSON.parse(storedUser));
              } catch {
                clearSession();
              }
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  /**
   * Full login flow: authenticate → load preferences → persist state.
   * Returns { success, message } matching portalv2 behavior.
   */
  const login = async (apiName, username, password) => {
    const api = apiName.toLowerCase();

    try {
      // Step 1: Basic Auth
      const authResponse = await authAPI.login(api, username, password);
      const token = authResponse.data;

      // Step 2: Load user preferences & app list
      const prefResponse = await authAPI.loadApplication(api, username, password);
      const prefData = prefResponse.data;

      if (!prefData.success) {
        return { success: false, message: 'Your account does not have permissions for this app.' };
      }

      const d = prefData.data;
      const highestRole = resolveHighestRole(d.user.basicRoles || []);

      if (!highestRole) {
        return { success: false, message: 'Your account does not have permissions for this app.' };
      }

      const userData = {
        apiName: api,
        userName: username,
        token,
        url: determineURL(api),
        highestRole,
        userIsAdmin: highestRole === 'ROLE_ADMIN',
        userIsManager: highestRole === 'ROLE_MANAGER' || highestRole === 'ROLE_ADMIN',
        name: `${d.user.person.firstName} ${d.user.person.lastName}`,
        apps: d.user.apps || [],
        preferences: d.preferences || {},
        oneRiskUser: d,
        loginTime: new Date().toISOString(),
      };

      const titlebarImage = d.ui?.logo
        ? `data:image/jpeg;base64,${d.ui.logo}`
        : null;

      // Persist
      localStorage.setItem('portalUser', JSON.stringify(userData));
      localStorage.setItem('portalToken', token);
      localStorage.setItem('portalActiveApi', api);
      localStorage.setItem('portalLoginAt', String(Date.now()));
      localStorage.setItem('portalOperation', d.preferences?.companyName || '');
      if (titlebarImage) {
        localStorage.setItem('portalTitlebarImage', titlebarImage);
      }

      // Write to Ionic Storage (IndexedDB) for legacy app compatibility
      const ionicUser = {
        apiName: api,
        userName: username,
        password,
        token,
        url: determineURL(api),
        highestRole,
        userIsAdmin: highestRole === 'ROLE_ADMIN',
        userIsManager: highestRole === 'ROLE_MANAGER' || highestRole === 'ROLE_ADMIN',
        name: `${d.user.person.firstName} ${d.user.person.lastName}`,
        apps: d.user.apps || [],
        preferences: d.preferences || {},
        oneRiskUser: d,
      };
      try {
        await Promise.race([
          writeToIonicStorage([
            ['activeApi', api],
            ['operation', d.preferences?.companyName || ''],
            ['titlebarImage', titlebarImage || ''],
            ['token', token],
            ['user', ionicUser],
          ]),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Ionic Storage write timed out')), 3000)),
        ]);
      } catch (e) {
        console.warn('Failed to write to Ionic Storage:', e);
      }

      setUser(userData);
      return { success: true, message: '' };
    } catch (error) {
      let message = 'Login failed';
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401 || status === 404 || status === 500) {
          message = data?.failureMsg || data?.message || message;
        }
      }
      return { success: false, message };
    }
  };

  const logout = async () => {
    const activeApi = localStorage.getItem('portalActiveApi');
    clearSession();
    await clearIonicStorage();
    authAPI.logout();
    if (activeApi) {
      localStorage.setItem('portalCachedApi', activeApi);
    }
    setUser(null);
  };

  /**
   * Check if the current user's role meets or exceeds the required role.
   */
  const hasRole = (requiredRole) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.highestRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    return userLevel >= requiredLevel;
  };

  /**
   * Get the cached API name for pre-filling the login form.
   */
  const getCachedApi = () => {
    return localStorage.getItem('portalCachedApi') || '';
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, hasRole, isAuthenticated, getCachedApi }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
